import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User, AuthTokens, FilterState } from '../types';

interface AppContextType {
  // Authentication state
  isAuthenticated: boolean;
  user: User | null;
  tokens: AuthTokens | null;
  setAuthState: (user: User | null, tokens: AuthTokens | null) => void;
  logout: () => void;
  isInitializing: boolean;
  setIsInitializing: (initializing: boolean) => void;

  // Filter state
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  clearFilters: () => void;

  // Auto-refresh state
  refreshInterval: number;
  lastRefresh: Date | null;
  setLastRefresh: (date: Date) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [filters, setFiltersState] = useState<FilterState>({});
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const refreshInterval = 30000; // 30 seconds

  const setAuthState = useCallback((newUser: User | null, newTokens: AuthTokens | null) => {
    setUser(newUser);
    setTokens(newTokens);
    setIsAuthenticated(!!newUser && !!newTokens);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setTokens(null);
    setIsAuthenticated(false);
  }, []);

  const setFilters = useCallback((newFilters: FilterState) => {
    setFiltersState(newFilters);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  const value: AppContextType = {
    isAuthenticated,
    user,
    tokens,
    setAuthState,
    logout,
    isInitializing,
    setIsInitializing,
    filters,
    setFilters,
    clearFilters,
    refreshInterval,
    lastRefresh,
    setLastRefresh,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
